import { initializeGoogleAnalytics, initializeGoogleTagManagers } from '@exam-room/utils';
import * as Sentry from '@sentry/react';
import { ApolloProvider, createApolloClient } from '@shared/apollo';
import { getExternalForceLogout } from '@shared/auth';
import { LoggerProvider, type LogLevel, useLogger } from '@shared/logger';
import { useEffect } from 'react';
import { RouterProvider } from 'react-router-dom';

import { LoadingSpinner, ServiceUnavailable, TailwindIndicator } from './components';
import {
  APP_STATE,
  ConfigProvider,
  NetworkProvider,
  SnackbarProvider,
  useConfig,
  useFirebaseRemoteConfig,
} from './providers';
import router from './Router';
import { registerServiceWorker } from './services/serviceWorker';

function App() {
  const { analytics, graphqlURL } = useConfig();

  const logger = useLogger();

  // Register service worker for offline image caching
  useEffect(() => {
    registerServiceWorker()
      .then((registration) => {
        if (registration) {
          logger.info('[App] Service Worker registered successfully');
        }
      })
      .catch((error) => {
        logger.error('[App] Service Worker registration failed:', error);
        Sentry.captureException(error, {
          tags: {
            feature: 'service-worker',
          },
        });
      });
  }, []);

  if (
    analytics?.googleTagManagers &&
    analytics.googleTagManagers.length > 0 &&
    import.meta.env.PROD
  ) {
    initializeGoogleTagManagers(analytics.googleTagManagers);
  }

  if (
    analytics?.googleAnalyticsIds &&
    analytics.googleAnalyticsIds.length > 0 &&
    import.meta.env.PROD
  ) {
    initializeGoogleAnalytics(analytics.googleAnalyticsIds);
  }

  const client = createApolloClient(graphqlURL, undefined, {
    onUnauthorized: () => getExternalForceLogout()?.(),
  });

  return (
    <SnackbarProvider>
      <NetworkProvider>
        <ApolloProvider client={client}>
          <RouterProvider router={router} />
        </ApolloProvider>
      </NetworkProvider>
    </SnackbarProvider>
  );
}

function AppWithConfig() {
  const { config } = useFirebaseRemoteConfig();

  if (config.appState === APP_STATE.INITIALIZE) {
    return (
      <div className='grid h-dvh w-screen place-items-center'>
        <LoadingSpinner />
      </div>
    );
  }

  if (config.appState === APP_STATE.MAINTENANCE) {
    return <ServiceUnavailable />;
  }

  return (
    <LoggerProvider config={{ level: config.logLevel as LogLevel, enableConsole: true }}>
      <ConfigProvider appConfig={config}>
        <App />
        <TailwindIndicator />
      </ConfigProvider>
    </LoggerProvider>
  );
}

export default AppWithConfig;
